import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    FlatList,
    Image,
    TouchableOpacity,
    Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useCart } from '../../context/CartContext';
import MarketplaceFooter from '../../components/common/MarketplaceFooter';
import { getImageUrl } from '../../utils/imageUtils';

const CartScreen = ({ navigation }) => {
    const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();

    const renderItem = ({ item }) => (
        <View style={styles.cartItem}>
            <Image
                source={{ uri: getImageUrl(item.images?.[0] || item.image) || 'https://via.placeholder.com/100' }}
                style={styles.itemImage}
            />
            <View style={styles.itemDetails}>
                <Text style={styles.itemName} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.itemPrice}>KES {item.price}</Text>
                <View style={styles.quantityContainer}>
                    <TouchableOpacity
                        onPress={() => updateQuantity(item.id, item.quantity - 1)}
                        style={styles.qtyButton}
                    >
                        <Ionicons name="remove" size={14} color="#64748B" />
                    </TouchableOpacity>
                    <Text style={styles.qtyText}>{item.quantity}</Text>
                    <TouchableOpacity
                        onPress={() => updateQuantity(item.id, item.quantity + 1)}
                        style={styles.qtyButton}
                    >
                        <Ionicons name="add" size={14} color="#64748B" />
                    </TouchableOpacity>
                </View>
            </View>
            <TouchableOpacity
                onPress={() => removeFromCart(item.id)}
                style={styles.removeButton}
            >
                <Ionicons name="trash-outline" size={16} color="#EF4444" />
            </TouchableOpacity>
        </View>
    );

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Ionicons name="chevron-back" size={20} color="#111827" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Shopping Basket</Text>
                <View style={{ width: 40 }} />
            </View>

            {cartItems.length === 0 ? (
                <View style={styles.emptyContainer}>
                    <Ionicons name="cart-outline" size={48} color="#CBD5E1" />
                    <Text style={styles.emptyText}>Empty Basket</Text>
                    <TouchableOpacity
                        style={styles.shopButton}
                        onPress={() => navigation.navigate('Marketplace')}
                    >
                        <Text style={styles.shopButtonText}>Go to Shops</Text>
                    </TouchableOpacity>
                </View>
            ) : (
                <>
                    <FlatList
                        data={cartItems}
                        renderItem={renderItem}
                        keyExtractor={item => item.id.toString()}
                        contentContainerStyle={styles.listContent}
                    />
                    <View style={styles.footer}>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>Grand Total</Text>
                            <Text style={styles.totalAmount}>KES {getCartTotal().toLocaleString()}</Text>
                        </View>
                        <TouchableOpacity
                            style={styles.checkoutButton}
                            onPress={() => navigation.navigate('Checkout')}
                        >
                            <Text style={styles.checkoutText}>Checkout Now</Text>
                        </TouchableOpacity>
                    </View>
                </>
            )}
            <MarketplaceFooter activeTab="Cart" navigation={navigation} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB', // Uniform off-white background
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 60 : 45,
        paddingBottom: 12,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    backButton: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: '#F8FAFB',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    listContent: {
        padding: 16,
        paddingBottom: 20,
    },
    cartItem: {
        flexDirection: 'row',
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 10,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    itemImage: {
        width: 64,
        height: 64,
        borderRadius: 8,
        backgroundColor: '#F3F4F6',
    },
    itemDetails: {
        flex: 1,
        marginLeft: 10,
        justifyContent: 'center',
    },
    itemName: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 2,
    },
    itemPrice: {
        fontSize: 13,
        color: '#F59E0B',
        fontWeight: '800',
        marginBottom: 6,
    },
    quantityContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFB',
        borderRadius: 8,
        alignSelf: 'flex-start',
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    qtyButton: {
        padding: 4,
        paddingHorizontal: 6,
    },
    qtyText: {
        fontSize: 12,
        fontWeight: '800',
        marginHorizontal: 8,
        color: '#1E293B',
    },
    removeButton: {
        width: 32,
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    emptyText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#64748B',
        marginTop: 12,
        marginBottom: 20,
    },
    shopButton: {
        backgroundColor: '#111827',
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 12,
    },
    shopButtonText: {
        color: '#FFFFFF',
        fontWeight: '800',
        fontSize: 13,
    },
    footer: {
        backgroundColor: '#FFFFFF',
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    totalLabel: {
        fontSize: 14,
        fontWeight: '700',
        color: '#64748B',
    },
    totalAmount: {
        fontSize: 20,
        fontWeight: '900',
        color: '#111827',
    },
    checkoutButton: {
        backgroundColor: '#10B981',
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
    },
    checkoutText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
});

export default CartScreen;
